import {
  IconAlertTriangle,
  IconInfoCircle,
  IconRocket,
} from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";
import { SimpleModalVariant as ModalVariant } from "../Modal/modal.js";

type InfoBannerVariant =
  | typeof ModalVariant.Standard
  | typeof ModalVariant.Warning
  | "UPSELL";

interface InfoBannerProps {
  title: string;
  description?: string | ReactNode;
  icon?: React.ReactNode;
  variant?: InfoBannerVariant;
  /** Trailing controls acting on what the banner announces, e.g. a button. */
  actions?: ReactNode;
}

const infoBannerStyle = tv({
  slots: {
    wrapper: "rounded-md border p-4",
    title: "text-sm font-medium",
    description: "mt-2 text-sm",
  },
  variants: {
    variant: {
      [ModalVariant.Warning]: {
        wrapper: "border-red-400 bg-red-50",
        title: "text-red-800",
        description: "text-red-700",
      },
      UPSELL: {
        wrapper: "border-violet-400 bg-violet-50",
        title: "text-violet-800",
        description: "text-violet-700",
      },
      [ModalVariant.Standard]: {
        wrapper: "border-blue-400 bg-blue-50",
        title: "text-blue-800",
        description: "text-blue-700",
      },
    },
  },
  defaultVariants: {
    variant: ModalVariant.Standard,
  },
});

const getDefaultIcon = (variant: InfoBannerVariant | undefined): ReactNode => {
  switch (variant) {
    case ModalVariant.Warning:
      return (
        <IconAlertTriangle
          aria-hidden="true"
          className="h-5 w-5 text-red-400"
        />
      );
    case "UPSELL":
      return (
        <IconRocket aria-hidden="true" className="h-5 w-5 text-violet-400" />
      );
    case ModalVariant.Standard:
    default:
      return (
        <IconInfoCircle aria-hidden="true" className="h-5 w-5 text-blue-400" />
      );
  }
};

export const InfoBanner: FC<InfoBannerProps> = ({
  title,
  description,
  icon,
  variant,
  actions,
}) => {
  const styles = infoBannerStyle({ variant });

  return (
    <div className={styles.wrapper()}>
      <div className="flex">
        <div className="shrink-0">{icon || getDefaultIcon(variant)}</div>
        {/* `flex-1` only alongside actions — without them nothing competes for
            the row, and growing would change how every existing banner lays
            out its content. */}
        <div className={twMerge("ml-3", actions && "flex-1")}>
          <h3 className={styles.title()}>{title}</h3>
          {description ? (
            <div className={styles.description()}>{description}</div>
          ) : null}
        </div>
        {actions ? <div className="ml-3 shrink-0">{actions}</div> : null}
      </div>
    </div>
  );
};
